import { ApolloClient, gql } from 'apollo-boost';
import {
  FeedbackStatus,
  ReportStage,
} from 'donor/portal/pages/FeedbackDashboard/api';
import { reportFilterKeys, reportQuickFilterKeys } from './utils';
import { FeedbackType } from '../NPOFeedback/SubmitReady/utils';
import { ReportingType } from './NewReport/api';
import { removeConnections } from 'shared/apollo/client';
import { ReportVisibility } from 'shared/auth/hooks/useUserActions';
import {
  AllocationReportDatesFragment,
  allocationReportDatesFragment,
} from 'shared/apollo/fragments/allocation';

export interface VariablesForCountFilter {
  searchText: string;
  filter: typeof reportFilterKeys[number];
  quickFilter: typeof reportQuickFilterKeys[number];
}

type Group = 'donor' | 'year' | 'program' | 'manager';
export type FilterGroups = {
  [x in Group]?: string;
}[];

interface Variables extends VariablesForCountFilter {
  first: number | undefined;
  offset: number | undefined;
  groupFilter: FilterGroups;
}

export interface ReportAPIResponse {
  feedbackType: FeedbackType;
  year: string | number;
  visibility: ReportVisibility;
  donorName: string | null;
  skipFeedbackFlag: string; // 'true' | 'false'
  relationshipManagerId: number | null;
  relationshipManagerEmail: string | null;
  relationshipManagerFirstName: string | null;
  relationshipManagerLastName: string | null;
  manager: '' | string;
  budget: number;
  dbId: number;
  errors: number;
  name: string;
  program: {
    Id: number;
    name: string;
    managerId: number;
  };
  successStory?: string;
  lessonLearned?: string;
  customQuestionResponses?: string;
  reportDescription?: string;
  numAllocations: number;
  stage: ReportStage;
  status: FeedbackStatus;
  totalDonations: number;
  warnings: number;
  version: 'v4' | null;
  reportingType?: ReportingType | null;
  optedInFullReporting?: boolean | null;
  results: {
    attachment: null;
    attachmentId: null;
    classification: string;
    currentForecastCatalyzed: number;
    currentForecastFullClaim: number;
    currentForecastFundedClaim: number;
    dbId: number;
    description: string;
    enabled: boolean;
    forecastCurrent: number | null;
    forecastInitial: number | null;
    forecastVariance: number;
    id: string;
    indicator: {
      benchmarkType: null;
      customizable: boolean;
      unit?: string | '{{beneficiaryTerm}}';
      definitionStart: string;
      definitionEnd: string;
      serviceGoal?: string;
      initialForecastStart: string;
      initialForecastEnd: string;
      initialMeasurementStart: string;
      initialMeasurementEnd: string;
      impactReceiptText?: string;
      outcome?: string;
      forecastOutcome?: string;
      finalOutcome?: string;
      finalForecastStart: string;
      finalForecastEnd: string;
      finalMeasurementStart: string;
      finalMeasurementEnd: string;
      isHuman: boolean;
      dbId: number;
      definition: string;
      group: null;
      guidance: string;
      id: string;
      measurementType: null;
      measurementTypeId: number;
      name: string;
      stageId: number;
      stgName: string;
      version: 'v3' | 'v4';
    };
    indicatorId: number;
    leverage: number;
    relevant: boolean;
    reportId: number;
    seen: boolean;
    sroiPer10kFullClaim: number;
    sroiPer10kFundedClaim: number;
    successCriteria: string;
  }[];
  beneficiaryDemographics: {
    bdtName: string;
    category: string;
    demographicId: number;
    description: string;
    id: string;
    percentage: number;
    reportId: number;
  }[];
  startDate: string;
  endDate: string;
  beneficiaryDescription: string;
  beneficiaryLocations?: { id: string; location: string }[];
  costCommentDirectCash: string;
  costCommentDirectInKind: string;
  costCommentIndirectCash: string;
  costCommentIndirectInKind: string;
  costCurrentDirectCash: number;
  costCurrentDirectInKind: number;
  costCurrentIndirectCash: number;
  costCurrentIndirectInKind: number;
  costInitialDirectCash: number;
  costInitialDirectInKind: number;
  costInitialIndirectCash: number;
  costInitialIndirectInKind: number;
  interventionDosageLevel: string;
  interventionDosageNumber: string;
  interventionDosageType: string;
  notes: {
    createdAt: string;
    id: string;
    message: string;
    type: 'RESULTS';
  }[];
  primaryInterventionDescription: null;
  primaryInterventionSubtypes: string; // JSON
  primaryInterventionType: string; // JSON
  taxonomySubjectId: string;
  taxonomySubjectLevel: string; // JSON
}

const baseReportQuery = (
  includeBeneficiaryLocations = true,
  extraNodeFields = '',
) => `
  edges {
    node {
      ${extraNodeFields}
      feedbackType
      skipFeedbackFlag
      enabled
      year
      donorName
      visibility
      relationshipManagerId
      relationshipManagerEmail
      relationshipManagerFirstName
      relationshipManagerLastName
      dbId
      name
      program {
        Id
        name
        managerId
        visibility
      }
      successStory
      lessonLearned
      customQuestionResponses
      reportDescription
      status
      stage
      budget
      totalDonations
      numAllocations
      manager
      errors
      warnings
      version
      reportingType
      optedInFullReporting

      results {
        edges {
          node {
            id
            enabled
            reportId
            seen
            indicatorId
            successCriteria
            forecastInitial
            forecastCurrent
            classification
            description
            relevant
            indicator {
              id
              name
              stageId
              definition
              customizable
              unit
              definitionStart
              definitionEnd
              serviceGoal
              initialForecastStart
              initialForecastEnd
              initialMeasurementStart
              initialMeasurementEnd
              impactReceiptText
              outcome
              forecastOutcome
              finalOutcome
              finalForecastStart
              finalForecastEnd
              finalMeasurementStart
              finalMeasurementEnd
              isHuman
              guidance
              group
              measurementTypeId
              benchmarkType
              version
              measurementType {
                id
                name
              }
              dbId
              stgName
            }
            forecastVariance
            currentForecastFundedClaim
            currentForecastFullClaim
            currentForecastCatalyzed
            leverage
            sroiPer10kFundedClaim
            sroiPer10kFullClaim
            dbId
            attachment
            attachmentId
          }
        }
      }
      startDate
      endDate
      beneficiaryDescription
      beneficiaryDemographics {
        edges {
          node {
            percentage
            reportId
            demographicId
            id
            bdtName
            category
            description
          }
        }
      }
      ${
        includeBeneficiaryLocations
          ? `
        beneficiaryLocations {
          edges {
            node {
              id
              location
            }
          }
        }
      `
          : ''
      }
      costCurrentDirectCash
      costCurrentDirectInKind
      costCurrentIndirectCash
      costCurrentIndirectInKind
      costInitialDirectCash
      costInitialDirectInKind
      costInitialIndirectCash
      costInitialIndirectInKind
      costCommentDirectCash
      costCommentDirectInKind
      costCommentIndirectCash
      costCommentIndirectInKind
      taxonomySubjectId
      taxonomySubjectLevel
      primaryInterventionType
      primaryInterventionSubtypes
      primaryInterventionDescription
      interventionDosageType
      interventionDosageLevel
      interventionDosageNumber
      notes {
        edges {
          node {
            createdAt
            id
            message
            type
          }
        }
      }
    }
  }
`;

const allReportsQuery = gql`
  query {
    reportsTodo {
      edges {
        node {
          enabled
          year
          dbId
          name
          stage
          status
          visibility
          reportingType
          level1DonorName
          relationshipManagerId
          relationshipManagerEmail
          relationshipManagerFirstName
          relationshipManagerLastName
          program {
            Id
            name
            managerId
            visibility
            level1DonorName
          }
        }
      }
    }
  }
`;

const reportStatusQuery = gql`
  query($filters: [ReportsOmniSearch!]) {
    reportsTodo(
      sort: UPDATED_AT_ASC
      filters: { and: $filters }
    ) {
      ${baseReportQuery(true, '...AllocationReportDatesFields')}
    }
  }
  ${allocationReportDatesFragment}
`;

const reportsQuery = (includeBeneficiaryLocations = true) => gql`
  query($first: Int, $offset: Int, $filters: [ReportsOmniSearch!]) {
    reportsTodo(
      sort: NAME_ASC
      first: $first
      offset: $offset
      filters: { and: $filters }
    ) {
      ${baseReportQuery(includeBeneficiaryLocations)}
    }
  }
`;

const reportByIdQuery = gql`
  query useReport($id: ID) {
    allReports(filters: {id: $id}) {
      ${baseReportQuery()}
    }
  }
`;

const findReportsByProgramAndYearQuery = gql`
  query useReport($program: String!, $year: Int!) {
    allReports(filters: {program: $program, year: $year}) {
      ${baseReportQuery()}
    }
  }
`;

const reportsCountQuery = ({
  searchText,
  filter,
  quickFilter,
}: VariablesForCountFilter) => {
  let queryString = '';
  switch (filter) {
    case 'Donor':
      queryString = 'donorReportCount';
      break;
    case 'Manager':
      queryString = 'managerReportCount';
      break;
    case 'Program':
      queryString = 'programReportCount';
      break;
    case 'Year':
      queryString = 'reportsCountByYear';
      break;
  }
  return gql`
      query {
        ${queryString}(${searchText ? `search: "${searchText}", ` : ''}
        archived: ${quickFilter ===
          'Include Archived'}, myReportsOnly: ${quickFilter ===
    'My Reports Only'}) {
          ${filter.toLowerCase()}
          count
        }
      }
    `;
};

export interface YearGroup {
  year: number;
  count: number;
}

export interface ProgramGroup {
  program: string;
  count: number;
}

export interface DonorGroup {
  donor: string;
  count: number;
}

export interface ManagerGroup {
  manager: string;
  count: number;
}

export type ReportsCountResponse =
  | {
      reportsCountByYear: YearGroup[];
    }
  | {
      programReportCount: ProgramGroup[];
    }
  | { donorReportCount: DonorGroup[] }
  | { managerReportCount: ManagerGroup[] };

export const getReportsCount = (
  client: ApolloClient<object>,
  variables: VariablesForCountFilter,
) => {
  return client.query({
    query: reportsCountQuery(variables),
    fetchPolicy: 'no-cache',
  });
};

const mapFilters = (variables: Variables) => {
  const filters: { [x: string]: string | number | boolean }[] = [];
  filters.push({ search: variables.searchText });
  filters.push({ enabled: 1 });

  if (variables.quickFilter === 'Include Archived') {
    filters.push({ myReportsOnly: true });
  } else if (variables.quickFilter === 'My Reports Only') {
    filters.push({ archived: true });
  }

  return {
    first: variables.first,
    offset: variables.offset,
    filters: [...filters, ...variables.groupFilter],
  };
};

export const getReportsApi = (
  client: ApolloClient<object>,
  variables: Variables,
  includeBeneficiaryLocations = true,
) => {
  return client.query<{
    reportsTodo: ReportAPIResponse[];
  }>({
    query: reportsQuery(includeBeneficiaryLocations),
    variables: mapFilters(variables),
    fetchPolicy: 'no-cache',
  });
};

export type ReportAllocations = {
  programId: number;
  year: number | string;
};

const generateFilters = (programYears: ReportAllocations[]): object[] => {
  const filters: object[] = [];
  programYears.forEach(({ programId, year }) => {
    filters.push({ and: [{ programId }, { year }] });
  });
  return [{ enabled: 1 }, { or: filters }];
};

export const getReportAllocations = (
  client: ApolloClient<object>,
  variables: ReportAllocations[],
) => {
  return client.query<{
    reportsTodo: ReportAPIResponse[];
  }>({
    query: reportsQuery(true),
    variables: {
      filters: generateFilters(variables),
    },
    fetchPolicy: 'network-only',
  });
};

export const getReportByIdApi = (
  client: ApolloClient<object>,
  reportId: number | string,
) => {
  return client.query({
    query: reportByIdQuery,
    variables: {
      id: reportId,
    },
    fetchPolicy: 'network-only',
  });
};

export interface AllReportYearsBase {
  enabled: boolean;
  year: number;
  dbId: number;
  name: string;
  visibility: ReportVisibility;
  stage: ReportStage;
  status: FeedbackStatus;
  relationshipManagerId: number | null;
  relationshipManagerEmail: string | null;
  relationshipManagerFirstName: string | null;
  relationshipManagerLastName: string | null;
  level1DonorName?: string | null;
  program: {
    Id: number;
    name: string;
    managerId: number;
    level1DonorName?: string | null;
  };
}

export interface AllReportYears {
  [id: number]: {
    reports: AllReportYearsBase[];
    byYears: { [year: number]: AllReportYearsBase[] };
  };
  programs: {
    Id: number;
    name: string;
    managerId: number;
    level1DonorName?: string | null;
  }[];
}

const reportsByProgram = (r: AllReportYearsBase[]) => {
  const programs = new Map<
    number,
    {
      Id: number;
      name: string;
      managerId: number;
      level1DonorName?: string | null;
    }
  >();
  const result: AllReportYears = { programs: [] };
  r.forEach((e: AllReportYearsBase) => {
    if (!e.program) {
      return;
    }
    if (!result[e.program.Id]) {
      result[e.program.Id] = {
        reports: [],
        byYears: {},
      };
    }
    if (!programs.has(e.program.Id)) {
      programs.set(e.program.Id, e.program);
    }
    result[e.program.Id].reports.push(e);
    if (!result[e.program.Id].byYears[e.year]) {
      result[e.program.Id].byYears[e.year] = [];
    }
    result[e.program.Id].byYears[e.year].push(e);
  });
  result.programs = Array.from(programs.values());
  return result;
};

export const getAllReports = (
  client: ApolloClient<object>,
): Promise<{
  data: AllReportYears;
}> => {
  return client
    .query({
      query: allReportsQuery,
      fetchPolicy: 'network-only',
    })
    .then(r => {
      const results = reportsByProgram(removeConnections(r.data.reportsTodo));
      return {
        data: results,
      };
    });
};

const deleteReportMutation = gql`
  mutation deleteReport($id: Int!) {
    deleteReport(id: $id) {
      success
    }
  }
`;

const disableReportMutation = gql`
  mutation disableReport($reportId: Int!) {
    disableReport(reportId: $reportId) {
      success
    }
  }
`;

const disableProgramMutation = gql`
  mutation disableProgram($id: Int!) {
    disableProgram(id: $id) {
      success
    }
  }
`;

export const disableProgramApi = (client: ApolloClient<object>, id: number) => {
  return client.mutate({
    mutation: disableProgramMutation,
    fetchPolicy: 'no-cache',
    variables: {
      id,
    },
  });
};

export const deleteReportApi = (client: ApolloClient<object>, id: number) => {
  return client.mutate({
    mutation: disableReportMutation,
    fetchPolicy: 'no-cache',
    variables: {
      reportId: id,
    },
  });
};

export type ReportTodo = {
  reportsTodo: (ReportAPIResponse & AllocationReportDatesFragment)[];
};

export const getReportsForTodo = (client: ApolloClient<object>) => {
  return client.query<ReportTodo>({
    query: reportStatusQuery,
    fetchPolicy: 'no-cache',
    variables: {
      filters: [
        {
          enabled: 1,
        },
        {
          or: [
            { status: 'TI Feedback Provided' },
            { status: 'Funder Feedback Provided' },
            { status: 'Interim Updates Needed' },
            { status: 'Final Updates Needed' },
            { status: 'Published' },
          ],
        },
      ],
    },
  });
};

export const findReportsByProgramAndYearApi = (
  client: ApolloClient<object>,
  program: string,
  year: number,
) => {
  return client.query({
    query: findReportsByProgramAndYearQuery,
    fetchPolicy: 'no-cache',
    variables: {
      program,
      year,
    },
  });
};
